// Manager / trainer sign-off (POST /assignments/{id}/signoff) with an optional
// note. Which button shows follows the viewer's role as the PARENT resolved
// it (manager kind: learning:assign in reach or manage; trainer kind: the
// session trainer or manage — never the learner) and the policy's needs:
// a kind already signed is shown as done, a kind the policy does not ask for
// is hidden. The server re-checks all of it.
import { useT } from '../lib/i18n';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, Loader2, PenLine } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import type { CompletionView } from '@/services/learningService';
import { useSignoffAssignment } from '../api/hooks';

type Kind = 'manager' | 'trainer';
interface Props {
  view: CompletionView;
  canManagerSign: boolean;
  canTrainerSign: boolean;
  className?: string;
  idPrefix?: string;
}

export const SignoffButtons: React.FC<Props> = ({ view, canManagerSign, canTrainerSign, className, idPrefix = 'so' }) => {
  const t = useT();
  const signoff = useSignoffAssignment();
  const [open, setOpen] = useState<Kind | null>(null);
  const [note, setNote] = useState('');
  const reqs = view.state?.requirements || view.policy?.requirements || [];
  const wants = (k: Kind) => reqs.some((r) => r.kind === `${k}_signoff`);
  const signed = (k: Kind) => (view.signoffs || []).some((s) => s.kind === k);
  const kinds: Kind[] = (['manager', 'trainer'] as Kind[]).filter((k) => wants(k) && (k === 'manager' ? canManagerSign : canTrainerSign));
  if (kinds.length === 0 || view.status === 'cancelled') return null;
  const label = (k: Kind) => (k === 'manager' ? t('training:signoff_manager_btn', 'Manager sign-off') : t('training:signoff_trainer_btn', 'Trainer sign-off'));

  return (
    <div className={`space-y-2 ${className || ''}`} data-testid="signoff-buttons">
      <div className="flex flex-wrap gap-1.5">
        {kinds.map((k) => signed(k) ? (
          <span key={k} className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2.5 py-1 text-xs font-medium text-success-strong" data-testid={`signed-${k}`}><CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> {label(k)}</span>
        ) : (
          <Button key={k} size="sm" variant="outline" disabled={signoff.isPending || open === k} onClick={() => { setOpen(k); setNote(''); }} data-testid={`signoff-${k}`}>
            <PenLine className="mr-1 h-3.5 w-3.5" aria-hidden />{label(k)}
          </Button>
        ))}
      </div>
      {open && (
        <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-2 space-y-2" data-testid="signoff-form">
          <div className="space-y-1">
            <Label htmlFor={`${idPrefix}-note`} className="text-xs">{t('training:signoff_note', 'Note (optional, kept with the sign-off)')}</Label>
            <Input id={`${idPrefix}-note`} className="h-8" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(null)} disabled={signoff.isPending}>{t('training:cancel', 'Cancel')}</Button>
            <Button size="sm" disabled={signoff.isPending} data-testid="signoff-confirm" onClick={() => signoff.mutate(
              { assignmentId: view.assignment_id, body: { kind: open, ...(note.trim() ? { note: note.trim() } : {}) } },
              { onSuccess: () => { toast.success(t('training:signoff_ok', 'Signed off')); setOpen(null); setNote(''); } },
            )}>{signoff.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:signoff_confirm', 'Sign off')}</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignoffButtons;
